// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import type { RendererDefinition } from '@/app-shell/types/workspace'
import { listAgents, listAgentKnowledgeBases, createAgent, updateAgent, deleteAgent, listAgentTemplates, instantiateAgent, promoteAgent, getPromotePreview } from '../api'
import type { Agent, AgentKnowledgeBase, AgentTemplate, CreateAgentPayload, InstantiateAgentPayload, PromoteAgentPayload, PromoteAgentPreview, UpdateAgentPayload } from '../types'
import { rendererRef } from '../rendererRef'

const agentsKey = (orgId: string, wsId: string) => ['agents', orgId, wsId] as const

/** Seed a just-created agent into the cached list so navigation to it lands
 *  before the background refetch arrives (otherwise AgentsPage redirects away). */
function seedAgent(queryClient: ReturnType<typeof useQueryClient>, orgId: string, wsId: string, created: Agent) {
  queryClient.setQueryData<Agent[]>(agentsKey(orgId, wsId), prev => {
    if (!prev) return [created]
    return prev.some(a => a.agentId === created.agentId) ? prev : [...prev, created]
  })
}

export function useAgents(apiClient: NodeApiClient, orgId: string, wsId: string) {
  return useQuery<Agent[]>({
    queryKey: agentsKey(orgId, wsId),
    queryFn: () => listAgents(apiClient, orgId, wsId),
    enabled: Boolean(orgId && wsId),
    staleTime: 5 * 60_000,
  })
}

export function useCreateAgent(apiClient: NodeApiClient, orgId: string, wsId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAgentPayload) => createAgent(apiClient, orgId, wsId, payload),
    onSuccess: (created) => {
      seedAgent(queryClient, orgId, wsId, created)
      void queryClient.invalidateQueries({ queryKey: agentsKey(orgId, wsId) })
    },
  })
}

export function useUpdateAgent(apiClient: NodeApiClient, orgId: string, wsId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, payload }: { agentId: string; payload: UpdateAgentPayload }) =>
      updateAgent(apiClient, orgId, wsId, agentId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentsKey(orgId, wsId) })
      // Template edits run with the app-workspace coords, but the library page
      // lists templates under the user's workspace key (see useDeleteAgent).
      void queryClient.invalidateQueries({ queryKey: ['agent-templates'] })
    },
  })
}

export function useDeleteAgent(apiClient: NodeApiClient, orgId: string, wsId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, deleteKnowledgeBases }: { agentId: string; deleteKnowledgeBases?: boolean }) =>
      deleteAgent(apiClient, orgId, wsId, agentId, deleteKnowledgeBases),
    onSuccess: () => {
      // Deletion cascades to the agent's pipeline, datasets, and (optionally)
      // KB instances — invalidate every cache that surfaces those.
      void queryClient.invalidateQueries({ queryKey: agentsKey(orgId, wsId) })
      void queryClient.invalidateQueries({ queryKey: ['pipelines', 'list', orgId, wsId] })
      void queryClient.invalidateQueries({ queryKey: ['kb-instances', orgId, wsId] })
      // Template deletes run with the app-workspace coords, but the library
      // page lists templates under the user's workspace key — invalidate the
      // whole prefix so deleted templates disappear without a refresh.
      void queryClient.invalidateQueries({ queryKey: ['agent-templates'] })
    },
  })
}

const agentTemplatesKey = (orgId: string, wsId: string) =>
  ['agent-templates', orgId, wsId] as const

export function useAgentTemplates(apiClient: NodeApiClient, orgId: string, wsId: string) {
  return useQuery<AgentTemplate[]>({
    queryKey: agentTemplatesKey(orgId, wsId),
    queryFn: () => listAgentTemplates(apiClient, orgId, wsId),
    enabled: Boolean(orgId && wsId),
    staleTime: 5 * 60_000,
  })
}

export function useInstantiateAgent(apiClient: NodeApiClient, orgId: string, wsId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: InstantiateAgentPayload) =>
      instantiateAgent(apiClient, orgId, wsId, payload),
    onSuccess: (created) => {
      seedAgent(queryClient, orgId, wsId, created)
      // Instantiation creates an agent plus a pipeline, KB instances,
      // datasets, and renderers — invalidate every cache that surfaces
      // those in the agent settings UI.
      void queryClient.invalidateQueries({ queryKey: agentsKey(orgId, wsId) })
      void queryClient.invalidateQueries({ queryKey: ['pipelines', 'list', orgId, wsId] })
      void queryClient.invalidateQueries({ queryKey: ['kb-instances', orgId, wsId] })
      void queryClient.invalidateQueries({ queryKey: ['workspaceConfig', orgId, wsId] })
    },
  })
}

export function usePromoteAgent(apiClient: NodeApiClient, orgId: string, wsId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, payload }: { agentId: string; payload: PromoteAgentPayload }) =>
      promoteAgent(apiClient, orgId, wsId, agentId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentTemplatesKey(orgId, wsId) })
      // Promotion creates an agent in the application workspace, whose agents
      // list the template settings page reads — this hook only knows the user's
      // coords, so invalidate the whole prefix.
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
    },
  })
}

/**
 * A renderer definition tagged with the agent that owns it. The agent is needed
 * to read the renderer's datasets: analysis results are stored per agent, and a
 * dataset key is only unique within one.
 */
export type WorkspaceRenderer = RendererDefinition & { agentId: string }

/**
 * Aggregate renderers from every agent in a workspace.
 * Use when a dashboard needs the full set of renderer definitions across agents
 * (e.g. legislative-review browsers' column set).
 *
 * Keyed by `rendererRef(agentId, rendererId)`, not by the bare renderer id:
 * renderer ids are agent-scoped, so keying by the bare id silently drops one of
 * two agents that both define, say, `analysis`.
 */
export function useWorkspaceRenderers(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
): Record<string, WorkspaceRenderer> {
  const { data: agents = [] } = useAgents(apiClient, orgId, wsId)
  return useMemo(() => {
    const merged: Record<string, WorkspaceRenderer> = {}
    for (const agent of agents) {
      for (const [key, def] of Object.entries(agent.renderers)) {
        merged[rendererRef(agent.agentId, key)] = { ...def, agentId: agent.agentId }
      }
    }
    return merged
  }, [agents])
}

export function useAgentKnowledgeBases(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  enabled = true,
) {
  return useQuery<AgentKnowledgeBase[]>({
    queryKey: ['agents', orgId, wsId, agentId, 'knowledge-bases'] as const,
    queryFn: () => listAgentKnowledgeBases(apiClient, orgId, wsId, agentId),
    enabled: enabled && Boolean(orgId && wsId && agentId),
    staleTime: 5 * 60_000,
  })
}

export function usePromotePreview(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  enabled = true,
) {
  return useQuery<PromoteAgentPreview>({
    queryKey: ['agents', orgId, wsId, agentId, 'promote-preview'] as const,
    queryFn: () => getPromotePreview(apiClient, orgId, wsId, agentId),
    enabled: enabled && Boolean(orgId && wsId && agentId),
    staleTime: 30_000,
  })
}
